import { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';
import http from './http';
import { mesmaListaDeMensagens } from '../utils/mesmaListaDeMensagens';
import { mensagensMescladas } from '../utils/mensagensMescladas';
import { getWaitingRooms } from '../redux/room/room.actions';
import { notifyNewMessage, notifyFileReceived } from '../redux/notification/notification.actions';
import { NOTIFICATION_TYPES } from '../redux/notification/notification.types';

const NEW_CHAT_MESSAGE_EVENT = "NEW_CHAT_MESSAGE_EVENT"; // Nome do evento
const MESSAGE_READ_EVENT = "MESSAGE_READ_EVENT";
const MESSAGE_DELIVERED_EVENT = "MESSAGE_DELIVERED_EVENT";
const SENDING_MESSAGE_EVENT = "SENDING_MESSAGE_EVENT";
const NEW_FILE_EVENT = "NEW_FILE_EVENT";
const SOCKET_SERVER_URL = process.env.REACT_APP_WS_URL;
const NEW_CLIENT_CONNECTED = "NEW_CLIENT_CONNECTED";

/**
 * O canal de uma sala de chat: o socket, as mensagens e as funções de envio.
 *
 * ## Todo evento abaixo devolve a CONVERSA INTEIRA
 *
 * Leitura, entrega, mensagem nova, arquivo novo: o servidor responde com todas
 * as mensagens da sala, não com o que mudou. Por isso cada `setMessages` passa
 * por `mesmaListaDeMensagens`: quando o conteúdo é igual ao que já está na
 * tela, devolve o estado anterior e o React desiste do render.
 *
 * Isso importa porque as idas ao servidor vêm em rajada. Abrir uma sala com 10
 * mensagens não lidas dispara 10 confirmações de leitura (uma por mensagem, em
 * `Room.messageReadFocus`) e mais uma confirmação de entrega por mensagem não
 * entregue -- cada uma respondendo com a conversa toda e sendo retransmitida
 * para todos na sala. Sem a comparação, são dezenas de re-renderizações
 * completas do chat desenhando a mesma tela.
 *
 * O NÚMERO de requisições continua o mesmo: ele é desenho do protocolo entre
 * api e ws (`PUT /messages/read` responde a sala toda), e reduzi-lo mexeria na
 * integração entre os três repositórios.
 */
/** Quantas mensagens cada pedida traz. É o mesmo padrão da api (`limit`). */
export const MENSAGENS_POR_PAGINA = 30;

/**
 * Esta sala tem um id utilizável?
 *
 * O canal de saída do `RoomList` existe antes de qualquer conversa estar
 * selecionada, e aí `roomId` é `undefined`. O socket CONTINUA conectando nesse
 * caso -- o servidor de ws trata um handshake sem sala de propósito, para o
 * operador poder enviar de uma tela sem sala fixa --, mas buscar mensagens não
 * faz sentido: virava `GET /messages/undefined`, um 400 e um
 * "Erro ao obter mensagens" no console a cada carga da tela de chat. (Antes
 * disto acontecia uma vez por LINHA da lista.)
 *
 * Mesma guarda que o clientweb já usa: a string "undefined" também chega aqui,
 * porque o valor passa pela query do socket.
 */
const salaConsultavel = (roomId) =>
  !!roomId && roomId !== 'undefined' && roomId !== 'null' && roomId !== '';

const useChat = (roomId, room) => {
  const [messages, setMessages] = useState([]); // Mensagens enviadas e recebidas
  const [sendingMessage, setsendingMessage] = useState(false);
  const socketRef = useRef();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.currentUser);

  /*
   * O HISTÓRICO MAIS ANTIGO, sob demanda.
   *
   * `GET /messages/{id}` é paginado e a tela só pedia a página 1: a conversa
   * abria com as 30 últimas e não havia como ver o resto. O botão "Carregar
   * mais mensagens..." existia, mas pendurado num `hasMore` que vinha da fatia
   * `whatsapp` do redux -- alimentada só pela camada whatsapp-web.js, removida
   * em 31/08. Ele nunca apareceu, e a rolagem até o topo também não fazia
   * nada.
   *
   * `totalDeMensagens` vem do próprio paginador. É ele, e não um booleano
   * guardado, que responde "tem mais?": comparar com o que está na tela
   * funciona mesmo quando o socket substitui a lista pela conversa INTEIRA,
   * que é o que quase todo evento faz.
   */
  const [totalDeMensagens, setTotalDeMensagens] = useState(0);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);
  const paginaCarregadaRef = useRef(1);
  // Guarda de concorrência em `ref`, e não em estado: duas rolagens até o topo
  // acontecem no mesmo quadro, e o estado só chegaria no render seguinte --
  // tarde demais para impedir a segunda pedida.
  const carregandoRef = useRef(false);

  /** O maior total já visto. Nunca diminui por causa de uma resposta atrasada. */
  const anotarTotal = (quantidade) =>
    setTotalDeMensagens((anterior) => Math.max(anterior || 0, quantidade || 0));

  useEffect(() => {
    // Cria uma conexão WebSocket
    socketRef.current = io(SOCKET_SERVER_URL, {
      query: { roomId },
    });

    // Confirma ENTREGA (estilo WhatsApp) das mensagens recebidas do CLIENTE (type 0)
    // que ainda não foram entregues nem lidas. Só a instância "primária" do socket
    // (a que recebe o objeto `room`) confirma, evitando envios duplicados quando há
    // várias instâncias do useChat para a mesma sala.
    const confirmDelivery = (msgs) => {
      if (!room || !socketRef.current) return;
      (msgs || []).forEach((m) => {
        if (m && m.type === 0 && !m.read_at && (!m.meta || (m.meta.ack ?? 0) < 2)) {
          socketRef.current.emit(MESSAGE_DELIVERED_EVENT, { messageId: m.id, senderId: socketRef.current.id });
        }
      });
    };

    socketRef.current.on('connect', () => {
      // Sem sala, não há o que buscar -- ver `salaConsultavel`. A conexão
      // acima continua acontecendo, porque é dela que depende o envio a
      // partir de uma tela sem sala fixa.
      if (!salaConsultavel(roomId)) return;

      paginaCarregadaRef.current = 1;

      http
        .get(`/messages/${roomId}`)
        .then(response => {
          // Extrair o array de mensagens do response paginado
          const messagesArray = response.data && response.data.data ? response.data.data : [];
          // Ordenar mensagens por data de criação (mais antigas primeiro)
          const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          setMessages((anteriores) => mesmaListaDeMensagens(anteriores, sortedMessages) ? anteriores : sortedMessages);
          // O paginador diz quantas a sala TEM. É isso que responde "tem mais?".
          anotarTotal(response.data?.total ?? sortedMessages.length);
          confirmDelivery(sortedMessages);
        })
        .catch(err => console.error("Erro ao obter mensagens:", err));
    });

    // Escuta as mensagens recebidas
    socketRef.current.on(NEW_CHAT_MESSAGE_EVENT, (messages) => {
      // Verificar se messages é um array ou se precisa extrair do data
      const messagesArray = Array.isArray(messages) ? messages : (messages && messages.data ? messages.data : []);
      // Ordenar mensagens por data de criação (mais antigas primeiro)
      const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMessages((anteriores) => mesmaListaDeMensagens(anteriores, sortedMessages) ? anteriores : sortedMessages);
      // Estes eventos devolvem a conversa INTEIRA, entao o tamanho dela e o total.
      anotarTotal(sortedMessages.length);
      confirmDelivery(sortedMessages);

      // Adicionar notificação para novas mensagens
      // if (messagesArray.length > 0) {
      //   const lastMessage = messagesArray[messagesArray.length - 1];
      //   // Verificar se a mensagem não é do próprio usuário logado e se o usuário está logado
      //   if (lastMessage && lastMessage.type !== 1 && currentUser && currentUser.id) { // Não notificar mensagens do operador
      //     dispatch(notifyNewMessage(
      //       roomId,
      //       lastMessage.message || 'Nova mensagem recebida',
      //       lastMessage.sender_name,
      //       lastMessage.id
      //     ));
      //   }
      // }
    });
    socketRef.current.on(NEW_FILE_EVENT, (messages) => {
      // Verificar se messages é um array ou se precisa extrair do data
      const messagesArray = Array.isArray(messages) ? messages : (messages && messages.data ? messages.data : []);
      // Ordenar mensagens por data de criação (mais antigas primeiro)
      const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMessages((anteriores) => mesmaListaDeMensagens(anteriores, sortedMessages) ? anteriores : sortedMessages);
      // Estes eventos devolvem a conversa INTEIRA, entao o tamanho dela e o total.
      anotarTotal(sortedMessages.length);
      confirmDelivery(sortedMessages);

      // Adicionar notificação para novos arquivos
      if (messagesArray.length > 0) {
        const lastMessage = messagesArray[messagesArray.length - 1];
        // Verificar se o arquivo não é do próprio usuário logado e se o usuário está logado
        if (lastMessage && lastMessage.type !== 1 && currentUser && currentUser.id) { // Não notificar arquivos do operador
          dispatch(notifyFileReceived(
            roomId,
            lastMessage.archive?.name || 'Arquivo',
            lastMessage.sender_name
          ));
        }
      }
    });
    socketRef.current.on(MESSAGE_READ_EVENT, (messages) => {
      // Verificar se messages é um array ou se precisa extrair do data
      const messagesArray = Array.isArray(messages) ? messages : (messages && messages.data ? messages.data : []);
      // Ordenar mensagens por data de criação (mais antigas primeiro)
      const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMessages((anteriores) => mesmaListaDeMensagens(anteriores, sortedMessages) ? anteriores : sortedMessages);
      // Estes eventos devolvem a conversa INTEIRA, entao o tamanho dela e o total.
      anotarTotal(sortedMessages.length);
    });
    socketRef.current.on(MESSAGE_DELIVERED_EVENT, (messages) => {
      // Atualiza os status de entrega (dois tiques cinza) recebidos do servidor.
      const messagesArray = Array.isArray(messages) ? messages : (messages && messages.data ? messages.data : []);
      const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMessages((anteriores) => mesmaListaDeMensagens(anteriores, sortedMessages) ? anteriores : sortedMessages);
      // Estes eventos devolvem a conversa INTEIRA, entao o tamanho dela e o total.
      anotarTotal(sortedMessages.length);
    });
    socketRef.current.on(SENDING_MESSAGE_EVENT, data => {
      if (data.type === 0) {
        setsendingMessage(true);
        setsendingMessage(false);
      }
    });
    socketRef.current.on(NEW_CLIENT_CONNECTED, () => {
      dispatch(getWaitingRooms());
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, [roomId, dispatch, currentUser]);

  /**
   * Busca a página seguinte -- as mensagens MAIS ANTIGAS do que as da tela.
   *
   * A ordenação da api é decrescente, então "página 2" é o bloco anterior ao
   * que já está desenhado. A junção é por id (`mensagensMescladas`), e não um
   * `[...antigas, ...atuais]`, porque a paginação é por OFFSET: se chegar
   * mensagem nova entre uma pedida e outra, tudo desce uma posição e o
   * primeiro item da página 2 é o último da página 1. Repetido, não pulado --
   * pular exigiria apagar mensagem, e nada apaga.
   *
   * Não devolve nada: quem chama só precisa que a lista cresça.
   */
  const carregarMaisAntigas = useCallback(async () => {
    if (carregandoRef.current || !salaConsultavel(roomId)) return;

    carregandoRef.current = true;
    setCarregandoAntigas(true);

    try {
      const proxima = paginaCarregadaRef.current + 1;
      const resposta = await http.get(`/messages/${roomId}`, {
        params: { page: proxima, limit: MENSAGENS_POR_PAGINA },
      });

      const antigas = Array.isArray(resposta.data?.data) ? resposta.data.data : [];
      paginaCarregadaRef.current = proxima;
      anotarTotal(resposta.data?.total);

      if (antigas.length > 0) {
        setMessages((atuais) => mensagensMescladas(atuais, antigas));
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens anteriores:', err);
    } finally {
      carregandoRef.current = false;
      setCarregandoAntigas(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Envia uma mensagem para o servidor
  const sendMessage = async (messageBody, user, id, type, whatsapp = null) => {

    let bodyContent = messageBody;

    if (messageBody instanceof FormData) {
      const rawMessage = messageBody.get('message');
      bodyContent = typeof rawMessage === 'string' ? rawMessage : '';
    }

    const messageData = {
      body: bodyContent,
      senderId: socketRef.current.id,
      operatorId: room?.operator_id ?? user.id,
      roomId: roomId ?? id,
      type,
      picture: user.picture ? user.picture : null,
      closing_message: user?.service_group?.text_config?.closing_message || null,
      welcome_message: user?.service_group?.text_config?.welcome_message || null,
      user_id: user.id,
    };

    // CUIDADO: este `whatsapp` NAO e "a sala e de WhatsApp?", e trocar por
    // esse criterio nao e a limpeza que parece.
    //
    // O que ele escolhe nao e o canal de entrega. Quem decide se a mensagem
    // vai para o WhatsApp e o SERVIDOR, pelo telefone da sala:
    // `MessageService::store()` chama `entregarNoWhatsAppSeForOCaso()`, que
    // olha `rooms.whatsapp_user_number` e despacha `EnviarRespostaDoOperador`.
    // Os dois ramos daqui acabam gravando a mensagem, entao os dois entregam.
    //
    // O que muda entre eles e o BROADCAST. O ramo `null` emite
    // `NEW_CHAT_MESSAGE_EVENT`, que e como a fala do operador aparece ao vivo
    // na tela de quem esta na sala. O `else` nao emite: ele faz
    // `POST /messages` e depois `send_whatsapp_message`, evento da camada
    // whatsapp-web.js que ninguem mais escuta. Mover a decisao para o
    // telefone jogaria toda conversa de WhatsApp no ramo sem broadcast -- a
    // mensagem chegaria no cliente e sumiria da tela do operador.
    //
    // E o `else` NAO e ramo morto, ao contrario do que o emit sugere:
    // `Chat/index.jsx:103` e `:117` passam `user.name` (string, portanto
    // truthy) e caem nele hoje, com tipo 4 -- que esta em
    // `TIPOS_QUE_O_CLIENTE_RECEBE`, ou seja, sao as mensagens de
    // inatividade, que CHEGAM no cliente por este caminho.
    //
    // (A linha 181 tambem cai aqui, mas e tipo 5 -- o recado interno do
    // operador, que a mesma constante exclui de proposito. Ela nao serve
    // de prova de carga viva, e citar assim ja foi um erro deste
    // comentario.)
    //
    // Mexer aqui remaneja trafego real.
    if (whatsapp == null) {
      if (messageBody instanceof FormData) {
        // Processar arquivos (áudio, imagem, etc.) para mensagens não-WhatsApp
        try {
          const response = await http.post(`/messages/file`, messageBody, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          });

          const dbMessageId = response.data[response.data.length - 1].id;

          const file = messageBody.get('file');
          const fileName = messageBody.get('message'); // nome original do arquivo

          const toBase64 = (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result);
              reader.onerror = (error) => reject(error);
            });

          const base64DataUrl = await toBase64(file);
          const base64Data = base64DataUrl.split(',')[1];
          const mimeType = base64DataUrl.split(';')[0].split(':')[1];

          // Emitir evento para arquivo não-WhatsApp
          socketRef.current.emit(NEW_FILE_EVENT, {
            ...messageData,
            dbMessageId,
            name: user.name,
            file: {
              mimetype: file.type,
              data: base64Data,
              filename: file.name,
              filesize: file.size
            }
          });

        } catch (error) {
          console.error("❌ Erro ao salvar arquivo no banco:", error);
        }
      } else {
        // Processar mensagens de texto normais
        if (type === 1) {
          messageData.type = 1;
          messageData.name = user.name;
          messageData.picture = user.picture;
          messageData.welcome_message = user?.service_group?.text_config?.welcome_message || null;
          messageData.closing_message = user?.service_group?.text_config?.closing_message || null;
        } else {
          messageData.type = type;
          messageData.name = user.name;
        }
        socketRef.current.emit(NEW_CHAT_MESSAGE_EVENT, messageData);
      }
    } else {
      try {
        if (messageBody instanceof FormData) {
          // if ((messageBody.startsWith('data:audio') || messageBody.startsWith('data:image')) && whatsapp) {
          const formData = new FormData();
          // formData.set('message', messageData.body);
          // formData.set('file', messageBody);
          // formData.set('room_id', messageData.roomId);
          // formData.set('type', type);
          // formData.set('user_id', messageData.operatorId);

          const response = await http.post(`/messages/file`, messageBody, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          });

          const dbMessageId = response.data[response.data.length - 1].id;

          const file = messageBody.get('file');
          const fileName = messageBody.get('message'); // nome original do arquivo

          const toBase64 = (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result);
              reader.onerror = (error) => reject(error);
            });

          const base64DataUrl = await toBase64(file);
          const base64Data = base64DataUrl.split(',')[1];
          const mimeType = base64DataUrl.split(';')[0].split(':')[1];


          socketRef.current.emit(
            'send_whatsapp_audio',
            {
              ...messageData,
              dbMessageId,
              name: user.name,
              file: {
                mimetype: file.type,
                data: base64Data,
                filename: file.name,
                filesize: file.size
              }
            }
          );

        } else {
          // Para mensagens de texto normais
          const response = await http.post(`/messages`, {
            message: messageData.body,
            room_id: messageData.roomId,
            type: messageData.type,
            user_id: messageData.operatorId
          });


          const dbMessageId = response.data[response.data.length - 1].id;

          socketRef.current.emit('send_whatsapp_message', {
            ...messageData,
            dbMessageId,
            name: user.name
          });
        }
      } catch (error) {
        console.error("❌ Erro ao salvar mensagem no banco:", error);
      }
    }
  };

  const sendFile = () => {
    socketRef.current.emit(NEW_FILE_EVENT, {});
  };

  const readMessage = (id, whatsapp, message) => {
    if (whatsapp)
      socketRef.current.emit(MESSAGE_READ_EVENT, {
        whatsapp: true,
        messageId: id,
        remoteId: message.room_id,
        body: message.message,
        meta: message.meta,
        archive: message.archive,
      });
    else
      socketRef.current.emit(MESSAGE_READ_EVENT, {
        messageId: id,
        senderId: socketRef.current.id
      });
  };

  const sendingMessageEvent = (event) => {
    switch (event) {
      case 'typing':
        socketRef.current.emit(SENDING_MESSAGE_EVENT, { type: 1 });
        break;
      case 'stopped_typing':
        socketRef.current.emit(SENDING_MESSAGE_EVENT, { type: 2 });
        break;
      default:
        break;
    }
  };

  return {
    messages,
    sendMessage,
    readMessage,
    sendingMessageEvent,
    sendingMessage,
    sendFile,
    // O historico mais antigo, sob demanda. `totalDeMensagens` e o que
    // responde "tem mais?" -- comparar com o que esta na tela.
    totalDeMensagens,
    carregandoAntigas,
    carregarMaisAntigas
  };
};

export default useChat;
